<template>
  <div>
    <div class="pagePopMask pFull" v-if="visible" @click="handleCancel"></div>
    <div class="pagePop w1280 pCenter" v-if="visible">
      <div class="w">
        <div class="tit fnt_32">{{ dynamicTitle }}</div>

        <div class="form-container">
          <div class="stform" v-if="!loading">
            <a-form-model ref="multiLanguageForm" :model="formData" :rules="formRules">
              <!-- 新增：队长姓名固定显示区域 -->
              <div v-if="captainName" class="field-group">
                <div class="field-title">{{ isTeenagerMode ? '领队' : '队长姓名' }}</div>
                <a-row :gutter="16">
                  <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" v-for="language in supportedLanguages" :key="language.code">
                    <a-form-model-item :label="language.name">
                      <a-input
                        :value="getCaptainNameByLanguage(language.code)"
                        :disabled="!isLeader"
                        :placeholder="`${isTeenagerMode ? '领队' : '队长姓名'}(${language.name})`"
                        @input="(e) => updateCaptainName(language.code, e.target.value)"
                      />
                    </a-form-model-item>
                  </a-col>
                </a-row>
                <div class="line"></div>
              </div>

              <!-- 动态渲染字段 -->
              <template v-if="fieldConfigs.length > 0">
                <div v-for="(field, index) in fieldConfigs" :key="field.id" class="field-group">
                  <!-- 在青少年比赛中，如果是领队且是姓名字段，则不显示 -->
                  <!-- 在非青少年比赛中，保持原有逻辑 -->
                  <div v-if="!shouldHideField(field)" class="field-title">
                    {{ getFieldNameByLanguage(field, currentSystemLanguage) }}
                  </div>
                  <a-row :gutter="16" v-if="!shouldHideField(field)">
                    <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" v-for="language in supportedLanguages" :key="language.code">
                      <a-form-model-item
                        :label="language.name"
                        :prop="`fieldValues.${field.id}.${language.code}`"
                      >
                        <!-- 根据字段类型渲染不同的输入组件 -->
                        <a-textarea
                          v-if="field.fieldType === 'textarea'"
                          v-model="formData.fieldValues[field.id][language.code]"
                          :placeholder="`${globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${getFieldNameByLanguage(field, currentSystemLanguage)}(${language.name})`"
                          :rows="3"
                          :disabled="shouldFieldBeDisabled(field, language.code)"
                        />
                        <a-input
                          v-else
                          v-model="formData.fieldValues[field.id][language.code]"
                          :placeholder="`${globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${getFieldNameByLanguage(field, currentSystemLanguage)}(${language.name})`"
                          :disabled="shouldFieldBeDisabled(field, language.code)"
                        />

                        <!-- 如果是只读字段，显示提示信息 -->
                        <!-- <div v-if="field.isReadOnly" class="readonly-tip">
                          <a-icon type="info-circle" style="color: #1890ff; margin-right: 4px;" />
                          <span style="color: #666; font-size: 12px;">{{ globalDataArray.siteTemplateLanguage.team_info_readonly_tips || '团队信息由队长填写，队员不可修改' }}</span>
                        </div> -->
                      </a-form-model-item>
                    </a-col>
                  </a-row>
                  <!-- 分割线（最后一个字段不显示，且字段未被隐藏时才显示） -->
                  <div v-if="index < fieldConfigs.length - 1 && !shouldHideField(field)" class="line"></div>
                </div>
              </template>

              <!-- 如果没有字段配置 -->
              <a-row v-else>
                <a-col :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
                  <div style="text-align: center; padding: 40px; color: #999;">
                    {{ globalDataArray.siteTemplateLanguage.system_data_empty_tips || '暂无数据' }}
                  </div>
                </a-col>
              </a-row>
            </a-form-model>
          </div>

          <!-- 加载状态 -->
          <div v-else style="text-align: center; padding: 60px;">
            <a-spin size="large" />
            <div style="margin-top: 16px; color: #999;">{{ globalDataArray.siteTemplateLanguage.loading || 'loading...' }}</div>
          </div>
        </div>
        <div class="btnBox fnt_18">
          <div class="btn btn1" :class="{ 'btn-disabled': loading }" @click="handleSubmitClick">{{ globalDataArray.siteTemplateLanguage.save || '保存' }}</div>
          <div class="btn btn2" @click="handleCancel">{{ globalDataArray.siteTemplateLanguage.cancel || '取消' }}</div>
        </div>
        <div class="close" @click="handleCancel"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserFieldConfigs, saveUserFieldValues } from '@/api/person/certificate'
import Cookies from 'js-cookie'

export default {
  name: 'MultiLanguageInfo',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recordData: {
      type: Object,
      default: () => ({})
    },
    // 新增：青少年模式标识
    isTeenagerMode: {
      type: Boolean,
      default: false
    },
    // 新增：当前选中的团队成员信息
    currentMember: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      fieldConfigs: [],
      formData: {
        fieldValues: {}
      },
      formRules: {}, // 动态生成的验证规则
      loading: false
    }
  },
  computed: {
    // 支持的语言列表，从全局配置动态获取
    supportedLanguages() {
      let languages = []

      // 从全局配置获取支持的语言列表
      if (this.globalDataArray && this.globalDataArray.siteLanguage && this.globalDataArray.siteLanguage.length > 0) {
        languages = this.globalDataArray.siteLanguage.map(lang => ({
          code: lang.value,
          name: lang.title
        }))
      } else {
        // 如果全局配置不可用，使用默认配置
        languages = [
          { code: 'zh_CN', name: '中文' },
          { code: 'en_US', name: 'English' }
        ]
      }

      // 将当前系统语言排在第一位
      const currentLang = this.currentSystemLanguage
      const currentIndex = languages.findIndex(lang => lang.code === currentLang)
      if (currentIndex > 0) {
        const currentLangObj = languages.splice(currentIndex, 1)[0]
        languages.unshift(currentLangObj)
      }

      return languages
    },

    // 当前系统语言
    currentSystemLanguage() {
      // 优先从 Cookie 获取
      const cookieLang = Cookies.get('DEFAULT_LANG')
      if (cookieLang) {
        return cookieLang
      }

      // 然后从全局配置获取
      if (this.globalDataArray && this.globalDataArray.siteInfo && this.globalDataArray.siteInfo.language) {
        return this.globalDataArray.siteInfo.language
      }

      // 最后使用默认值
      return 'zh_CN'
    },
    // 新增：动态标题计算属性
    dynamicTitle() {
      const certificateLabel = this.certificateTypeLabel
      if (this.isTeenagerMode && this.currentMember && this.currentMember.personName) {
        return `${certificateLabel} - ${this.currentMember.personName}`
      }
      return certificateLabel
    },
    certificateTypeLabel() {
      const labelMap = {
        C: '完善证书信息',
        I: '完善教师证书信息',
        PU: '完善参赛单位证书信息'
      }
      return labelMap[this.recordData?.certificateType] ||
        this.globalDataArray.siteTemplateLanguage.improve_information2 ||
        '完善证书信息'
    },
    // 新增：判断是否有领队姓名
    hasLeaderName() {
      // 如果是队长，不需要检查领队姓名
      if (this.isLeader) {
        return false
      }

      // 青少年模式和成人比赛都从recordData获取领队信息
      if (this.recordData) {
        const leaderName = this.recordData.leader_name ||
                          this.recordData.leaderName ||
                          this.recordData.leaderNameNew
        return !!leaderName
      }

      return false
    },
    // 新增：判断是否是成人比赛的队长
    isAdultCaptain() {
      // 非青少年模式 && 是队长 && 有当前成员（说明是在编辑队员）
      return !this.isTeenagerMode && this.isLeader && this.currentMember
    },

    // 新增：获取队长姓名（用于判断是否显示队长姓名区域）
    captainName() {
      // 如果当前记录就是队长自己，不显示顶部的队长姓名区域
      if (this.isLeader) {
        return null
      }

      // 如果是队员，显示队长姓名区域
      if (this.recordData) {
        return this.recordData.leader_name ||
               this.recordData.leaderName ||
               this.recordData.leaderNameNew ||
               '队长'
      }

      return null
    },

    // 新增：判断当前成员是否是队长
    isLeader() {
      // 青少年模式：从currentMember获取
      if (this.isTeenagerMode && this.currentMember) {
        const isLeader = this.currentMember.isCaptain === 1
        console.log(`👑 青少年模式队长判断:`, {
          isTeenagerMode: this.isTeenagerMode,
          currentMemberIsCaptain: this.currentMember.isCaptain,
          isLeader: isLeader
        })
        return isLeader
      }

      // 成人比赛：直接从recordData获取
      if (this.recordData) {
        // 检查多个可能的队长标识字段
        const isCaptain = this.recordData.isCaptain === 1 ||
                          this.recordData.is_captain === 1 ||
                          this.recordData.isLeader === 1 ||
                          this.recordData.is_leader === 1
        const isLeader = isCaptain
        console.log(`🏆 成人比赛队长判断:`, {
          isTeenagerMode: this.isTeenagerMode,
          recordDataIsCaptain: this.recordData.isCaptain,
          recordDataIs_captain: this.recordData.is_captain,
          recordDataIsLeader: this.recordData.isLeader,
          recordDataIs_leader: this.recordData.is_leader,
          isLeader: isLeader
        })
        return isLeader
      }

      console.log(`❌ 无法判断队长身份:`, {
        isTeenagerMode: this.isTeenagerMode,
        hasCurrentMember: !!this.currentMember,
        hasRecordData: !!this.recordData
      })
      return false
    },

    // 新增：判断字段是否应该被禁用（基于权限控制）
    shouldFieldBeDisabled() {
      return (field, languageCode) => {
        // 🔒 新增：当当前语言是中文时，中文字段不可编辑
        if (this.currentSystemLanguage === 'zh_CN' && languageCode === 'zh_CN') {
          console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 当前语言是中文，中文字段不可编辑`)
          return true
        }

        // 青少年比赛模式的特殊权限控制
        if (this.isTeenagerMode) {
          // 如果是领队，所有字段都可以修改（即使有原始值）
          if (this.isLeader) {
            console.log(`✅ 字段启用 - ${field.fieldName || field.fieldCode}(${languageCode}): 青少年模式领队，所有字段可编辑`)
            return false
          }
          // 如果是队员
          else {
            // 队员姓名字段可以编辑
            if (this.isTeamMemberNameField(field)) {
              console.log(`✅ 字段启用 - ${field.fieldName || field.fieldCode}(${languageCode}): 青少年模式队员，可编辑队员姓名`)
              return false
            }
            // 领队姓名字段不可编辑（只读）
            if (this.isLeaderNameField(field)) {
              console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 青少年模式队员，领队字段不可编辑`)
              return true
            }
            // 其他字段，检查是否有原始值或只读
            if (this.hasOriginalValue(field, languageCode) || field.isReadOnly) {
              console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 青少年模式队员，有原始值或只读`)
              return true
            }
            console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 青少年模式队员，非姓名字段不可编辑`)
            return true
          }
        }

        // 非青少年比赛模式，保持原有逻辑
        console.log(`🏆 成人比赛模式权限检查:`, {
          isTeenagerMode: this.isTeenagerMode,
          isLeader: this.isLeader,
          fieldCode: field.fieldCode,
          fieldName: field.fieldName,
          recordDataIsCaptain: this.recordData?.isCaptain
        })

        // 如果是队长，所有字段都可以修改（即使有原始值）
        if (this.isLeader) {
          console.log(`✅ 字段启用 - ${field.fieldName || field.fieldCode}(${languageCode}): 成人比赛队长权限，所有字段可编辑`)
          return false
        }

        // 如果是队员，只能修改姓名字段（包括英文姓名字段）
        if (this.isUserNameField(field)) {
          console.log(`✅ 姓名字段启用 - ${field.fieldName || field.fieldCode}(${languageCode}): 队员可修改姓名`)
          return false
        }

        // 如果是队员且不是姓名字段，检查是否有原始值或只读
        if (this.hasOriginalValue(field, languageCode) || field.isReadOnly) {
          console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 队员权限，有原始值或只读`)
          return true
        }

        // 如果是队员且不是姓名字段，禁用
        console.log(`🔒 字段禁用 - ${field.fieldName || field.fieldCode}(${languageCode}): 队员不可修改非姓名字段`)
        return true
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadFieldConfigs()
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    // 语言编码归一化：兼容 en/en_US、zh/zh_CN 等不同站点配置
    normalizeLanguageCode(code) {
      if (!code) return code
      const c = String(code).trim()
      if (c === 'en') return 'en_US'
      if (c === 'zh') return 'zh_CN'
      return c
    },

    // 仅从已保存的 languageValues 中取值（不使用 originalValue 回退）
    // 用于避免“姓名”等字段在所有语言都被中文默认值覆盖，导致已保存英文不回显。
    getSavedLanguageValue(field, languageCode) {
      if (!field || !field.languageValues) return ''
      const langCode = this.normalizeLanguageCode(languageCode)
      const fallbackLangCode =
        langCode === 'en_US' ? 'en'
          : (langCode === 'zh_CN' ? 'zh' : null)

      return field.languageValues[langCode] ||
        (fallbackLangCode ? field.languageValues[fallbackLangCode] : '') ||
        ''
    },
    // 新增：判断是否应该隐藏字段
    shouldHideField(field) {
      // 青少年比赛模式
      if (this.isTeenagerMode) {
        // 青少年模式下，无论是领队还是队员，都隐藏领队姓名字段
        // 原因：
        // - 领队编辑时：编辑的是自己的姓名，不需要单独显示"领队"字段
        // - 队员编辑时：不需要显示队长姓名字段
        if (this.isLeaderNameField(field)) {
          console.log(`🚫 字段隐藏: ${field.fieldName || field.fieldCode} - 青少年模式，隐藏领队姓名字段`)
          return true
        }

        // 其他字段正常显示
        console.log(`✅ 字段显示: ${field.fieldName || field.fieldCode} - 青少年模式，显示其他字段`)
        return false
      }

      // 非青少年比赛模式（成人比赛）
      // 成人比赛中，显示所有字段
      console.log(`🏆 字段显示: ${field.fieldName || field.fieldCode} - 成人比赛，显示所有字段`)
      return false
    },
    async loadFieldConfigs() {
      console.log('🔍 开始加载字段配置，recordData:', this.recordData)
      console.log('👤 当前用户信息:', {
        isLeader: this.isLeader,
        isTeenagerMode: this.isTeenagerMode,
        currentMember: this.currentMember,
        recordData: this.recordData,
        recordDataIsCaptain: this.recordData?.isCaptain,
        currentMemberIsCaptain: this.currentMember?.isCaptain,
        hasLeaderName: this.hasLeaderName
      })
      console.log('👨‍🏫 领队信息检查:', {
        recordDataLeaderNames: {
          leader_name: this.recordData?.leader_name,
          leaderName: this.recordData?.leaderName,
          leaderNameNew: this.recordData?.leaderNameNew
        },
        currentMemberLeaderNames: {
          leaderName: this.currentMember?.leaderName,
          leader_name: this.currentMember?.leader_name,
          leaderNameNew: this.currentMember?.leaderNameNew,
          leaderNameMulti: this.currentMember?.leaderNameMulti
        }
      })
      if (!this.recordData.id) {
        console.error('❌ recordData.id 不存在:', this.recordData)
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
        return
      }

      // ✅ 修复：从报名记录中获取正确的比赛ID
      // 优先级：competitionId > competition_id > parent_competition_id
      const competitionId = this.recordData.competitionId ||
                         this.recordData.competition_id ||
                         this.recordData.parent_competition_id
      const certificateType = this.recordData.certificateType

      console.log('🆔 获取到的参数:', {
        signupId: this.recordData.id,
        competitionId: competitionId,
        certificateType: certificateType,
        recordData: this.recordData
      })

      if (!competitionId) {
        console.error('❌ competitionId 不存在:', this.recordData)
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
        console.error('报名记录数据:', this.recordData)
        return
      }
      if (!certificateType) {
        console.error('❌ certificateType 不存在:', this.recordData)
        this.$message.error('证书类型缺失，请重新选择证书类型')
        return
      }

      try {
        this.loading = true
        const params = {
          signupId: this.recordData.id,
          competitionId: competitionId,
          certificateType: certificateType
        }

        console.log('📡 调用API参数:', params)
        const res = await getUserFieldConfigs(params)
        console.log('📡 API响应:', res)
        if (res.success) {
          this.fieldConfigs = res.result.fieldInfos || []
          console.log('✅ 字段配置加载成功:', this.fieldConfigs)
          console.log('📊 fieldConfigs长度:', this.fieldConfigs.length)
          console.log('🔐 权限控制 - 当前用户身份:', this.isLeader ? '队长' : '队员')
          this.initFormData()
        } else {
          console.error('❌ API调用失败:', res)
          // 如果是"暂无数据"的特定错误，显示友好提示
          if (res.message === '暂无数据') {
            this.$message.info('该比赛暂未配置多语言证书字段，请联系管理员')
          } else {
            this.$message.error(res.message || (this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试'))
          }
        }
      } catch (error) {
        console.error('💥 加载字段配置异常:', error)
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
      } finally {
        this.loading = false
      }
    },

    initFormData() {
      console.log('🚀 开始初始化表单数据')
      console.log('📋 字段配置详情:', this.fieldConfigs)
      console.log('📋 字段数量:', this.fieldConfigs.length)
      console.log('📋 字段列表:', this.fieldConfigs.map(f => ({
        id: f.id,
        fieldCode: f.fieldCode,
        fieldName: f.fieldName,
        hasLanguageValues: !!f.languageValues,
        languageValuesKeys: f.languageValues ? Object.keys(f.languageValues) : []
      })))
      console.log('🌐 支持的语言:', this.supportedLanguages)
      console.log('👤 当前用户身份:', {
        isLeader: this.isLeader,
        isTeenagerMode: this.isTeenagerMode,
        hasLeaderName: this.hasLeaderName,
        isAdultCaptain: this.isAdultCaptain
      })

      // 初始化表单数据结构
      const fieldValues = {}

      this.fieldConfigs.forEach(field => {
        console.log(`🔄 处理字段: ${field.fieldName || field.fieldCode}`, {
          fieldCode: field.fieldCode,
          fieldId: field.id,
          hasLanguageValues: !!field.languageValues,
          languageValues: field.languageValues
        })
        fieldValues[field.id] = {}

        // 为每个支持的语言初始化值
        this.supportedLanguages.forEach(language => {
          let value = ''

          // ✅ 最高优先级：如果该字段该语言已有保存值，直接回显，避免被“中文默认值”覆盖
          const savedValue = this.getSavedLanguageValue(field, language.code)
          if (savedValue) {
            value = savedValue
            console.log(`✅ 回显已保存多语言值(${language.code}): ${value}`)
          } else if (this.isTeenagerMode) {
            if (this.isLeader) {
              // 领队编辑：显示领队自己的姓名
              if (this.isLeaderNameField(field)) {
                const leaderName = this.currentMember?.personName ||
                  this.currentMember?.leaderName ||
                  this.recordData?.leader_name ||
                  this.recordData?.leaderName ||
                  this.recordData?.leaderNameNew
                if (leaderName) {
                  value = leaderName
                }
              } else if (this.isCompanyNameField(field)) {
                const captainCompanyValue = this.getCaptainCompanyValue(field.fieldCode, language.code)
                if (captainCompanyValue) {
                  value = captainCompanyValue
                } else {
                  const companyName = this.recordData?.company ||
                    this.recordData?.companyName ||
                    this.recordData?.companyFullName ||
                    this.recordData?.company_full_name
                  value = companyName || this.getFieldValueForLanguage(field, language)
                }
              } else {
                value = this.getFieldValueForLanguage(field, language)
              }
            } else {
              // 队员视角：姓名用自身，领队/单位从队长或 recordData 回退
              if (this.isTeamMemberNameField(field)) {
                const memberName = this.currentMember?.personName ||
                  this.currentMember?.memberName ||
                  this.currentMember?.userName ||
                  this.currentMember?.userNameNew
                value = memberName || this.getFieldValueForLanguage(field, language)
              } else if (this.isLeaderNameField(field)) {
                const captainLeaderValue = this.getCaptainCompanyValue(field.fieldCode, language.code)
                if (captainLeaderValue) {
                  value = captainLeaderValue
                } else {
                  const leaderName = this.recordData?.leader_name ||
                    this.recordData?.leaderName ||
                    this.recordData?.leaderNameNew
                  value = leaderName || this.getFieldValueForLanguage(field, language)
                }
              } else if (this.isCompanyNameField(field)) {
                const captainCompanyValue = this.getCaptainCompanyValue(field.fieldCode, language.code)
                if (captainCompanyValue) {
                  value = captainCompanyValue
                } else {
                  const companyName = this.recordData?.company ||
                    this.recordData?.companyName ||
                    this.recordData?.companyFullName ||
                    this.recordData?.company_full_name
                  value = companyName || this.getFieldValueForLanguage(field, language)
                }
              } else {
                value = this.getFieldValueForLanguage(field, language)
              }
            }
          } else {
            // 非青少年比赛模式（成人比赛）
            if (this.currentMember && !this.isLeader && this.isUserNameField(field)) {
              const memberName = this.currentMember.personName ||
                this.currentMember.userName ||
                this.currentMember.userNameNew
              value = memberName || this.getFieldValueForLanguage(field, language)
            } else if (this.isAdultCaptain) {
              if (this.isUserNameField(field) && this.currentMember) {
                const memberName = this.currentMember.personName ||
                  this.currentMember.userName ||
                  this.currentMember.userNameNew
                value = memberName || this.getFieldValueForLanguage(field, language)
              } else {
                value = this.getFieldValueForLanguage(field, language)
              }
            } else if (this.hasLeaderName && this.isUserNameField(field)) {
              const leaderName = this.recordData?.leader_name ||
                this.recordData?.leaderName ||
                this.recordData?.leaderNameNew
              value = leaderName || this.getFieldValueForLanguage(field, language)
            } else if (this.isCompanyNameField(field)) {
              const captainCompanyValue = this.getCaptainCompanyValue(field.fieldCode, language.code)
              if (captainCompanyValue) {
                value = captainCompanyValue
              } else {
                const companyName = this.recordData?.company ||
                  this.recordData?.companyName ||
                  this.recordData?.companyFullName ||
                  this.recordData?.company_full_name
                value = companyName || this.getFieldValueForLanguage(field, language)
              }
            } else {
              value = this.getFieldValueForLanguage(field, language)
            }
          }

          fieldValues[field.id][language.code] = value
        })
      })

      this.formData.fieldValues = fieldValues

      // 初始化验证规则
      this.initFormRules()
    },

    handleSubmitClick() {
      if (this.loading) {
        return
      }
      this.handleSubmit()
    },

    async handleSubmit() {
      if (this.loading) return

      try {
        // 使用 Ant Design Vue 的表单验证，不显示错误消息，只触发字段验证
        await this.$refs.multiLanguageForm.validate()

        this.loading = true

        // 构造保存数据
        const fieldValues = []
        console.log('🔍 开始构造保存数据')
        console.log('📋 formData.fieldValues:', this.formData.fieldValues)

        Object.keys(this.formData.fieldValues).forEach(fieldId => {
          const languageValues = {}
          this.supportedLanguages.forEach(language => {
            const value = this.formData.fieldValues[fieldId][language.code]
            console.log(`📝 字段ID: ${fieldId}, 语言: ${language.code}, 值: "${value}"`)
            if (value && value.trim()) {
              const langCode = this.normalizeLanguageCode(language.code)
              languageValues[langCode] = value.trim()
            }
          })
          if (Object.keys(languageValues).length > 0) {
            fieldValues.push({
              fieldConfigId: fieldId,
              languageValues: languageValues
            })
            console.log(`✅ 添加字段值:`, { fieldConfigId: fieldId, languageValues })
          }
        })

        const competitionId = this.recordData.competitionId ||
                           this.recordData.competition_id ||
                           this.recordData.parent_competition_id
        const certificateType = this.recordData.certificateType
        const saveData = {
          signupId: this.recordData.id,
          competitionId: competitionId,
          certificateType: certificateType,
          fieldValues: fieldValues
        }

        console.log('📤 准备保存的数据:', JSON.stringify(saveData, null, 2))

        const res = await saveUserFieldValues(saveData)
        console.log('📥 保存响应:', res)

        if (res.success) {
          this.$message.success(this.globalDataArray.siteTemplateLanguage.system_save_success_tips || '保存成功')
          this.handleCancel()
        } else {
          console.error('❌ 保存失败:', res.message)
          this.$message.error(res.message || (this.globalDataArray.siteTemplateLanguage.save_failed || '保存失败'))
        }
      } catch (error) {
        // 验证失败时，不显示错误消息，让 Ant Design Vue 显示字段级别的验证提示
        return
      } finally {
        this.loading = false
      }
    },

    handleCancel() {
      this.$emit('update:visible', false)
      this.$emit('close') // 触发关闭事件，让父组件知道表单已关闭
      this.resetForm()
    },

    resetForm() {
      this.fieldConfigs = []
      this.formData = {
        fieldValues: {}
      }
      this.formRules = {}
    },

    // 检查字段是否有原始值
    hasOriginalValue(field, languageCode) {
      return field.originalValue && field.originalLanguage === languageCode
    },

    // 根据语言获取字段名称（成人比赛显示「队长」，青少年比赛显示「领队」）
    getFieldNameByLanguage(field, language) {
      let displayName = ''
      // 成人比赛中，领队姓名字段或名称含「领队」的字段统一显示为「队长」
      if (!this.isTeenagerMode && (this.isLeaderNameField(field) || (field.fieldName && field.fieldName.includes('领队')))) {
        if (language === 'en_US' || language === 'en') {
          displayName = this.globalDataArray?.siteTemplateLanguage?.team_captain_en || 'Captain'
        } else {
          displayName = this.globalDataArray?.siteTemplateLanguage?.team_captain || '队长'
        }
        return displayName
      }
      if (field.fieldNameMulti && field.fieldNameMulti[language]) {
        displayName = field.fieldNameMulti[language]
      } else {
        displayName = field.fieldName || ''
      }
      // 成人比赛中，任意字段名称若含「领队」也替换为「队长」
      if (!this.isTeenagerMode && displayName && displayName.includes('领队')) {
        displayName = displayName.replace(/领队/g, '队长')
      }
      return displayName
    },

    // 获取字段在指定语言下的值
    getFieldValueForLanguage(field, language) {
      console.log(`🔍 获取字段值 - ${field.fieldName || field.fieldCode}(${language.code}):`, {
        fieldCode: field.fieldCode,
        languageCode: language.code,
        languageValues: field.languageValues,
        languageValuesKeys: field.languageValues ? Object.keys(field.languageValues) : [],
        hasValue: field.languageValues && field.languageValues[language.code],
        originalValue: field.originalValue,
        originalLanguage: field.originalLanguage
      })

      // 优先级：已保存的多语言值 > 原始值 > 空值
      const langCode = this.normalizeLanguageCode(language.code)
      let fallbackLangCode = null
      if (langCode === 'en_US') {
        fallbackLangCode = 'en'
      } else if (langCode === 'zh_CN') {
        fallbackLangCode = 'zh'
      }

      if (field.languageValues && (field.languageValues[langCode] || (fallbackLangCode && field.languageValues[fallbackLangCode]))) {
        const val = field.languageValues[langCode] || field.languageValues[fallbackLangCode]
        console.log(`✅ 使用已保存的多语言值: ${val}`)
        return val
      } else if (field.originalValue && (field.originalLanguage === language.code || this.normalizeLanguageCode(field.originalLanguage) === langCode)) {
        // 如果有原始值且语言匹配，使用原始值
        console.log(`✅ 使用原始值（语言匹配）: ${field.originalValue}`)
        return field.originalValue
      } else if (field.originalValue && language.code === this.currentSystemLanguage && !field.originalLanguage) {
        // 如果有原始值但没有指定语言，默认作为当前系统语言
        console.log(`✅ 使用原始值（当前系统语言）: ${field.originalValue}`)
        return field.originalValue
      } else if (field.originalValue && language.code === 'zh_CN' && !field.originalLanguage && !this.currentSystemLanguage) {
        // 如果没有系统语言配置，默认作为中文
        console.log(`✅ 使用原始值（默认中文）: ${field.originalValue}`)
        return field.originalValue
      }
      console.log(`⚠️ 返回空值`)
      return ''
    },

    // 判断是否是队员姓名字段（userName、userNameNew）
    isTeamMemberNameField(field) {
      const isTeamMemberName = field.fieldCode === 'userName' ||
                             field.fieldCode === 'userNameNew'

      console.log(`👥 队员姓名字段识别: ${field.fieldName || field.fieldCode}`, {
        fieldCode: field.fieldCode,
        fieldName: field.fieldName,
        isTeenagerMode: this.isTeenagerMode,
        isTeamMemberName: isTeamMemberName
      })

      return isTeamMemberName
    },

    // 判断是否是领队姓名字段（leaderName、leaderNameNew）
    isLeaderNameField(field) {
      const isLeaderName = field.fieldCode === 'leaderName' ||
                            field.fieldCode === 'leaderNameNew'

      console.log(`👑 领队姓名字段识别: ${field.fieldName || field.fieldCode}`, {
        fieldCode: field.fieldCode,
        fieldName: field.fieldName,
        isLeaderName: isLeaderName
      })

      return isLeaderName
    },

    // 新增：判断是否是单位名称字段（company、companyFullName等）
    isCompanyNameField(field) {
      const isCompanyName = field.fieldCode === 'company' ||
                           field.fieldCode === 'companyFullName' ||
                           field.fieldCode === 'companyName' ||
                           field.fieldCode === 'company_full_name' ||
                           field.fieldCode === 'company_name'

      console.log(`🏢 单位名称字段识别: ${field.fieldName || field.fieldCode}`, {
        fieldCode: field.fieldCode,
        fieldName: field.fieldName,
        isCompanyName: isCompanyName
      })

      return isCompanyName
    },

    // 新增：从队长的多语言字段值中获取单位名称
    getCaptainCompanyValue(fieldCode, languageCode) {
      // 检查是否有队长的多语言字段值
      if (!this.recordData || !this.recordData.captainFieldValues) {
        return null
      }

      // 在队长的字段配置中查找对应的单位字段
      const captainField = this.recordData.captainFieldValues.find(f => f.fieldCode === fieldCode)
      if (!captainField || !captainField.languageValues) {
        return null
      }

      // 返回指定语言的值
      return captainField.languageValues[languageCode] || null
    },

    // 新增：获取队长姓名（按语言）
    getCaptainNameByLanguage(languageCode) {
      // 如果是队长自己编辑，从 captainNameValues 中获取（支持实时编辑）
      if (this.isLeader) {
        return this.captainNameValues[languageCode] || ''
      }

      // 如果是队员查看队长姓名，从 recordData.captainFieldValues 中获取队长的多语言姓名
      // 使用 getCaptainCompanyValue 方法查找队长的 userNameNew 或 userName 字段
      const captainNameValue = this.getCaptainCompanyValue('userNameNew', languageCode) ||
                               this.getCaptainCompanyValue('userName', languageCode) ||
                               this.getCaptainCompanyValue('UserNameNew', languageCode) ||
                               this.getCaptainCompanyValue('UserName', languageCode)

      if (captainNameValue) {
        console.log(`👑 从captainFieldValues获取队长姓名(${languageCode}): ${captainNameValue}`)
        return captainNameValue
      }

      // 如果没有多语言值，返回默认值（通常是中文）
      const defaultName = this.recordData?.leader_name ||
                         this.recordData?.leaderName ||
                         this.recordData?.leaderNameNew ||
                         ''
      console.log(`👑 从recordData获取队长姓名默认值(${languageCode}): ${defaultName}`)
      return defaultName
    },

    // 新增：更新队长姓名
    updateCaptainName(languageCode, value) {
      // 只有队长可以编辑
      if (!this.isLeader) {
        return
      }

      // 查找队长姓名字段
      const leaderField = this.fieldConfigs.find(f =>
        f.fieldCode === 'leaderName' || f.fieldCode === 'leaderNameNew'
      )

      if (leaderField && this.formData.fieldValues[leaderField.id]) {
        // 更新 formData 中的值
        this.formData.fieldValues[leaderField.id][languageCode] = value
        console.log(`✏️ 队长姓名已更新(${languageCode}): ${value}`)
      }
    },

    // 判断是否是姓名相关字段（包括领队和队员） - userName、userNameNew、leaderName、leaderNameNew
    isUserNameField(field) {
      // 使用 fieldCode 来判断，因为 getUserFieldConfigs 接口返回的是 fieldCode
      const isNameField = field.fieldCode === 'userName' ||
                         field.fieldCode === 'userNameNew' ||
                         field.fieldCode === 'leaderName' ||
                         field.fieldCode === 'leaderNameNew'

      console.log(`🏷️ 字段识别: ${field.fieldName || field.fieldCode}`, {
        fieldCode: field.fieldCode,
        fieldName: field.fieldName,
        isNameField: isNameField
      })

      return isNameField
    },

    // 初始化表单验证规则
    initFormRules() {
      const formRules = {}

      this.fieldConfigs.forEach(field => {
        this.supportedLanguages.forEach(language => {
          // 只有当字段是必填且未被禁用时才添加验证规则
          if (field.isRequired && !this.shouldFieldBeDisabled(field, language.code)) {
            formRules[`fieldValues.${field.id}.${language.code}`] = [{
              required: true,
              message: `${this.globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${this.getFieldNameByLanguage(field, this.currentSystemLanguage)}(${language.name})`,
              trigger: ['change', 'blur']
            }]
          }
        })
      })

      this.formRules = formRules
    }
  }
}
</script>

<style scoped>
/* 表单容器支持滚动 */
.form-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

/* 自定义滚动条样式 */
.form-container::-webkit-scrollbar {
  width: 6px;
}

.form-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.form-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.form-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 权限提示信息样式 */
.permission-tip {
  margin: 16px 0;
  padding: 12px 16px;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #52c41a;
}

.field-group {
  margin-bottom: 32px;
  padding: 0;
}

.field-title {
  font-size: 18px;
  font-weight: bold;
  color: #1890ff;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1890ff;
  position: relative;
}

.field-title::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 20px;
  height: 2px;
  background: #52c41a;
}

.line {
  height: 1px;
  background: #e8e8e8;
  margin: 24px auto;
  width: 80%;
}

/* 表单项样式优化 */
::v-deep .ant-form-item-label {
  font-weight: 600;
  color: #333;
}

::v-deep .ant-input,
::v-deep .ant-input:focus {
  border-radius: 6px;
  transition: all 0.3s ease;
}

::v-deep .ant-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 禁用状态的样式 */
::v-deep .ant-input[disabled],
::v-deep .ant-input-disabled {
  background-color: #f5f5f5;
  border-color: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

/* 复用已有的样式 */
.btn-disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

/* 只读提示信息样式 */
.readonly-tip {
  margin-top: 4px;
  padding: 4px 8px;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
</style>
